/**
 * Prefix substitution module.
 *
 * Pure function. Takes source text containing the canonical `ry` prefix
 * and rewrites every recognized surface to a user-chosen prefix.
 *
 * Surfaces rewritten (in this order, applied as separate passes):
 *   1. `Ry` PascalCase identifiers (`RyElement`, `RyButton`)        -> `{Pascal}Element`
 *   2. `RY_` SCREAMING_SNAKE prefix (`RY_COMPONENTS`)                -> `{UPPER}_COMPONENTS`
 *   3. `ry-` lowercase prefix everywhere (tags, classes, data-,
 *      --custom-props, @layer, string literals, BEM)                 -> `{prefix}-`
 *   4. `ry:` event name prefix (`ry:change`)                         -> `{prefix}:`
 *   5. `ry` standalone word (the `<ry>` wrapper, `querySelectorAll('ry')`,
 *      the `<template ry>` attribute)                                -> `{prefix}`
 *
 * Word boundaries (`\b`) protect against false positives in unrelated
 * identifiers (`category`, `mystery`, `Rylie`, etc).
 *
 * Same function is safe for CSS, HTML, JS, TS, JSON, and Markdown.
 *
 * Importable from both Node (build scripts) and the browser (customizer).
 */

export const CANONICAL_PREFIX = 'ry';

const VALID_PREFIX = /^[a-z][a-z0-9-]{0,15}$/;

export function validatePrefix(prefix) {
  if (typeof prefix !== 'string') return 'prefix must be a string';
  if (!VALID_PREFIX.test(prefix)) {
    return 'prefix must be lowercase letters/digits/hyphens, start with a letter, and be 1-16 chars';
  }
  if (prefix === CANONICAL_PREFIX) return null;
  // reserved single-letter html element words to avoid: a, b, i, p, s, u, q
  if (['a','b','i','p','s','u','q'].includes(prefix)) {
    return `"${prefix}" collides with a built-in HTML element name`;
  }
  return null;
}

function pascalCase(prefix) {
  // Convert "acme-ui" -> "AcmeUi"; "z" -> "Z"
  return prefix
    .split('-')
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ''))
    .join('');
}

function upperSnake(prefix) {
  return prefix.replace(/-/g, '_').toUpperCase();
}

/**
 * Substitute the canonical `ry` prefix in a single string with a new prefix.
 * No-op if prefix === 'ry'.
 *
 * @param {string} input  Source text.
 * @param {string} prefix Target prefix (e.g. 'z', 'acme', 'ui').
 * @returns {string} Rewritten text.
 */
export function substitute(input, prefix) {
  if (typeof input !== 'string') {
    throw new TypeError('substitute(input, prefix): input must be a string');
  }
  const err = validatePrefix(prefix);
  if (err) throw new Error(`invalid prefix: ${err}`);
  if (prefix === CANONICAL_PREFIX) return input;

  const Pascal = pascalCase(prefix);
  const UPPER = upperSnake(prefix);

  return input
    // 1. PascalCase identifiers: RyElement -> ZElement, RyColorPicker -> AcmeUiColorPicker
    .replace(/\bRy(?=[A-Z])/g, Pascal)
    // 2. SCREAMING_SNAKE constants: RY_COMPONENTS -> Z_COMPONENTS
    .replace(/\bRY_/g, `${UPPER}_`)
    // 3. Lowercase prefix: ry-*, --ry-*, data-ry-*, .ry-*, @layer ry-*, ry-*.ts, etc.
    //    \b ensures we don't match inside word characters (e.g. `category`)
    .replace(/\bry-/g, `${prefix}-`)
    // 4. Event name prefix: ry:change -> z:change
    .replace(/\bry:/g, `${prefix}:`)
    // 5. Standalone <ry> wrapper element + `ry` attribute on <template ry>
    .replace(/\bry\b/g, prefix);
}

/**
 * Rewrite a filename or path, replacing leading `ry-` or `ry.` in the
 * basename with the new prefix. Directories are left alone.
 *
 *   ry-element.ts        -> z-element.ts
 *   css/ry-tokens.css    -> css/z-tokens.css
 *   ry-ui.min.js         -> z-ui.min.js
 *   skills/ry-ui-builder -> skills/z-ui-builder
 *
 * @param {string} path
 * @param {string} prefix
 * @returns {string}
 */
export function substitutePath(path, prefix) {
  const err = validatePrefix(prefix);
  if (err) throw new Error(`invalid prefix: ${err}`);
  if (prefix === CANONICAL_PREFIX) return path;

  return path.replace(/(^|\/)ry(-|\.)/g, `$1${prefix}$2`);
}
